import type { JournalBusy, JournalCard, JournalColumn, JournalHold } from "./journal-model"

/**
 * Лента дня одного мастера — то, во что превращается сетка на телефоне.
 *
 * ЭТО НЕ СЖАТАЯ СЕТКА, А ДРУГАЯ РАСКЛАДКА (ТЗ §2.13). Дневная сетка по мастерам на 360
 * пикселях не воспроизводится вовсе: колонка в 150 px — это нижняя граница читаемости
 * имени, а их на телефоне помещается ровно две. Поэтому на телефоне показывается день
 * ОДНОГО мастера списком сверху вниз — и это основной рабочий экран мастера, а не
 * компромисс на случай маленького экрана.
 *
 * ЗАЧЕМ В ЛЕНТЕ ПУСТЫЕ ПРОМЕЖУТКИ. В сетке свободное время видно само — это пустое место
 * между карточками. В списке его нет: две карточки подряд выглядят как две подряд идущие
 * записи, даже если между ними два часа. Поэтому промежуток становится отдельной строкой
 * с длительностью: мастеру важно знать, успеет ли он пообедать (ТЗ §2.6).
 */

export type JournalListRow =
  | { readonly kind: "appointment"; readonly card: JournalCard; readonly isNext: boolean }
  | { readonly kind: "busy"; readonly block: JournalBusy }
  | { readonly kind: "hold"; readonly hold: JournalHold }
  | { readonly kind: "gap"; readonly startMinute: number; readonly endMinute: number }

/** Короче пяти минут промежуток не показывается: это не окно, а погрешность расписания. */
const MIN_GAP_MINUTES = 5

interface Interval {
  readonly startMinute: number
  readonly endMinute: number
}

function mergeIntervals(intervals: readonly Interval[]): Interval[] {
  const sorted = intervals.toSorted((a, b) => a.startMinute - b.startMinute)
  const merged: Interval[] = []
  for (const interval of sorted) {
    const last = merged[merged.length - 1]
    if (last !== undefined && interval.startMinute <= last.endMinute) {
      merged[merged.length - 1] = {
        startMinute: last.startMinute,
        endMinute: Math.max(last.endMinute, interval.endMinute),
      }
    } else {
      merged.push({ startMinute: interval.startMinute, endMinute: interval.endMinute })
    }
  }
  return merged
}

/**
 * Лента дня.
 *
 * @param nowMinute текущая минута по зоне филиала — по ней выбирается «следующий».
 */
export function buildDayList(column: JournalColumn, nowMinute: number): readonly JournalListRow[] {
  // Отменённая запись не занимает время: показывать после неё «свободно» и саму запись
  // одновременно было бы противоречием. Поэтому она в ленте есть, а в занятости — нет.
  const occupying: Interval[] = [
    ...column.cards
      .filter((card) => card.status !== "cancelled")
      .map((card) => ({ startMinute: card.startMinute, endMinute: card.endMinute })),
    ...column.busy.map((block) => ({
      startMinute: block.startMinute,
      endMinute: block.endMinute,
    })),
    ...column.holds.map((hold) => ({
      startMinute: hold.startMinute,
      endMinute: hold.endMinute,
    })),
  ]

  const gaps: JournalListRow[] = []
  const workStart = column.workStartMinute
  const workEnd = column.workEndMinute
  if (workStart !== null && workEnd !== null) {
    let cursor = workStart
    for (const interval of mergeIntervals(occupying)) {
      if (interval.endMinute <= workStart || interval.startMinute >= workEnd) continue
      if (interval.startMinute - cursor >= MIN_GAP_MINUTES) {
        gaps.push({ kind: "gap", startMinute: cursor, endMinute: interval.startMinute })
      }
      cursor = Math.max(cursor, interval.endMinute)
    }
    if (workEnd - cursor >= MIN_GAP_MINUTES) {
      gaps.push({ kind: "gap", startMinute: cursor, endMinute: workEnd })
    }
  }

  // «Следующий» — ближайшая запись, которая ещё не закончилась. Отменённая и неявка
  // следующими быть не могут: к ним никто не придёт.
  const upcoming = column.cards
    .filter(
      (card) =>
        card.endMinute > nowMinute &&
        (card.status === "pending" || card.status === "confirmed" || card.status === "in_progress"),
    )
    .toSorted((a, b) => a.startMinute - b.startMinute)
  const nextId = upcoming[0]?.id ?? null

  const rows: JournalListRow[] = [
    ...column.cards.map(
      (card): JournalListRow => ({ kind: "appointment", card, isNext: card.id === nextId }),
    ),
    ...column.busy.map((block): JournalListRow => ({ kind: "busy", block })),
    ...column.holds.map((hold): JournalListRow => ({ kind: "hold", hold })),
    ...gaps,
  ]

  return rows.toSorted((a, b) => startOf(a) - startOf(b))
}

function startOf(row: JournalListRow): number {
  switch (row.kind) {
    case "appointment":
      return row.card.startMinute
    case "busy":
      return row.block.startMinute
    case "hold":
      return row.hold.startMinute
    case "gap":
      return row.startMinute
  }
}

/**
 * ОБЩАЯ ЛЕНТА ДНЯ — «Все» на телефоне администратора (5-А1).
 *
 * ПОЧЕМУ ОНА ВООБЩЕ НУЖНА. Администратор в зале отвечает за весь салон, а не за одного
 * мастера: «кто следующий», «куда посадить пришедшего без записи», «у кого дыра в 15:00».
 * Лента одного мастера отвечала на это только перебором вкладок — шесть касаний, чтобы
 * понять текущий час.
 *
 * ЧЕГО ЗДЕСЬ НЕТ. Свободных промежутков: «свободно» у разных мастеров в одно и то же
 * время — это не одна строка, и склеивать их значит соврать. Промежутки остаются в ленте
 * ОДНОГО мастера, где они и означают то, что написано.
 *
 * НЕРАЗОБРАННЫЕ ЗАПИСИ ИДУТ В ОБЩЕЙ ЛЕНТЕ НАРАВНЕ С ОСТАЛЬНЫМИ. Запись без мастера —
 * это клиент, который придёт: спрятать её на отдельную вкладку значит потерять его
 * в тот единственный день, когда её забыли разобрать.
 */
export interface FeedRow {
  readonly row: JournalListRow
  /** Чей это час. null — запись без исполнителя: её ещё предстоит разобрать. */
  readonly employeeId: string | null
  readonly employeeName: string
  /** Цвет мастера из справочника — им подписана строка. null — цвет не задан. */
  readonly employeeColor: string | null
}

export function buildAllFeed(
  model: {
    readonly columns: readonly JournalColumn[]
    readonly unassigned: readonly JournalCard[]
  },
  nowMinute: number,
): readonly FeedRow[] {
  const rows: FeedRow[] = []

  for (const column of model.columns) {
    const employee = {
      employeeId: column.employee.id,
      employeeName: column.employee.displayName,
      employeeColor: column.employee.color,
    }
    for (const row of buildDayList(column, nowMinute)) {
      // Промежутки и системная занятость мастера в общей ленте не показываются:
      // обед одного мастера в списке всего салона — шум, а не ответ.
      if (row.kind === "gap" || row.kind === "busy") continue
      rows.push({ row, ...employee })
    }
  }

  for (const card of model.unassigned) {
    rows.push({
      row: { kind: "appointment", card, isNext: false },
      employeeId: null,
      employeeName: "Без мастера",
      employeeColor: null,
    })
  }

  return rows.toSorted((a, b) => startOf(a.row) - startOf(b.row))
}
